import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
// import NProgress from 'nprogress';

import EventBus from '@/utilities/event-bus';
import { useAuthStore } from '../../auth/store';
import { authApi } from '@/utilities/axios';
import { showToast } from '@/utilities/toast-handler';

const USER_URL = process.env.VUE_APP_API_VERSION + '/admin/users';
const USER_PERMISSION_URL = process.env.VUE_APP_API_VERSION + '/admin/user';

export const useUserStore = defineStore('user', () => {
    const authStore = useAuthStore();
    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId;
    });
    // states
    const editId = ref(null);
    const user = ref({
        name: null,
        surname: null,
        email: null,
        company_id: null,
        mail_otp: 0,
        avoids_sso_when_active: 0,
    });
    const users = ref([]);
    const userGroups = ref([]); // stores the set of permission groups that the user has access to
    const totalUsers = ref(null);
    const params = ref({
        page_size: 10,
        page: 1,
        sex: null,
        age_start: null,
        age_end: null,
        name: null,
        surname: null,
        dob_start: null,
        dob_end: null,
        email: null,
        reference: null,
        attribute: [],
        value: [],
    });
    const loading = ref(false);
    const activePanelUsers = ref([]); // Specifically for UsersSelectionDialog panel

    // helpers
    const initForm = () => {
        editId.value = null;
        user.value.name = null;
        user.value.surname = null;
        user.value.email = null;
        user.value.company_id = null;
        user.value.mail_otp = 0;
        user.value.avoids_sso_when_active = 0;
    };

    const initUsers = () => {
        params.value = {
            page_size: 10,
            page: 1,
            sex: null,
            age_start: null,
            age_end: null,
            name: null,
            surname: null,
            dob_start: null,
            dob_end: null,
            email: null,
            reference: null,
            attribute: [],
            value: [],
        };
        users.value = [];
    };

    // joins name and surname with key as fullname
    const addFullNameToUserArray = (data) => {
        if (data && data.length) {
            return data?.map((datum) => {
                return {
                    ...datum,
                    fullname: datum.name.charAt(0).toUpperCase() + datum.name.slice(1) + ' ' + datum.surname.charAt(0).toUpperCase() + datum.surname.slice(1),
                };
            });
        }
        return [];
    };

    // actions
    const fetchAllUsers = async (userParams, allData = false, paginate = true) => {
        try {
            // NProgress.start();
            loading.value = true;
            users.value = [];
            const response = await authApi.get(USER_URL, {
                params: {
                    ...userParams,
                    paginate:paginate,
                    company_id: company_id.value,
                },
            });
            const data = response.data;
            // this part will only be triggered once if allData is true
            if (allData && data.total !== data.data.length) {
                // if the length and total doesnt matches then make a same request with the page_size as the overall total
                // we will still send the allData= true flag so that we can move to the else part of this block
                await fetchAllUsers({ page_size: data.total }, false);
            } else {
                users.value = addFullNameToUserArray(data.data?.filter((user) => user?.company_id != null));
                totalUsers.value = data.total;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch users');
        } finally {
            loading.value = false;
            // NProgress.done();
        }
    };

    // Create a new user
    const createUser = async () => {
        try {
            const params = {
                ...user.value,
                company_id: company_id.value,
            };
            const response = await authApi.post(USER_URL, params);
            if (response.statusCode === 201) {
                users.value.push(response.data);
                // Clean the form data after successful operation
                initForm();
                showToast(response.toast, 'User has been created successfully');
                return true;
            }
        } catch (error) {
            showToast({ severity: 'warn', summary: 'Invalid Form Data' }, error?.data?.message ?? 'Failed to create a user');
        }
    };

    // Load User data to edit
    const editUser = (userData) => {
        editId.value = userData.id;
        user.value.name = userData.name;
        user.value.surname = userData.surname;
        user.value.email = userData.email;
        user.value.company_id = userData.company_id;
        user.value.avoids_sso_when_active = userData.avoids_sso_when_active == 1 ? true : false;
    };

    // Update existing new user
    const updateUser = async () => {
        try {
            const payload = {
                ...user.value,
                company_id: company_id.value,
                frozen: 0,
                avoids_sso_when_active: user.value.avoids_sso_when_active ? 1 : 0,
            };
            const response = await authApi.patch(`${USER_URL}/${editId.value}`, payload);
            if (response.statusCode === 200) {
                // Clean the form data after successful operation
                initForm();
                showToast(response.toast, 'User has been updated successfully');
                return true;
            }
        } catch (error) {
            showToast({ severity: 'warn', summary: 'Invalid Form Data' }, error?.data?.message ?? 'Failed to create a user');
        }
    };
    // Add User to the departments
    const addUserToDepartments = async (userId, departments) => {
        try {
            const URL = `${USER_URL}/${userId}/department?companyId=${company_id.value}`;
            const response = await authApi.post(URL, {
                departments: [departments],
            });
            if (response.statusCode === 200) {
                showToast(response.toast, 'Users added to the department');
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to add users into the department');
        }
    };

    // fetch all permission groups related to the user
    const fetchUserPermissionGroups = async (userId) => {
        try {
            EventBus.emit('show-operation-loader');
            const response = await authApi.get(`${USER_PERMISSION_URL}/${userId}/groups`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                userGroups.value = response.data?.groups;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch user permission groups');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    // set permission groups to a user
    const setUserPermissionGroups = async (userId, groups) => {
        try {
            EventBus.emit('show-operation-loader');
            const response = await authApi.post(
                `${USER_PERMISSION_URL}/${userId}/groups`,
                { groups },
                {
                    params: {
                        company_id: company_id.value,
                    },
                }
            );
            if (response.statusCode === 200) {
                showToast(response.toast, "User's permission groups updated ");
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? "Failed to update user's permission groups");
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    // delete user
    const deleteUser = async (userId) => {
        try {
            EventBus.emit('show-operation-loader');
            const response = await authApi.delete(`${USER_URL}/${userId}`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                showToast(response.toast, 'User has been deleted');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to delete the user');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    // getters
    const usersList = computed(() => {
        return users.value?.map((user) => {
            return {
                id: user.id,
                name: user.name.charAt(0).toUpperCase() + user.name.slice(1) +
                    ' ' +
                    (user.surname ? user.surname.charAt(0).toUpperCase() + user.surname.slice(1) : ''),
            };
        }) || [];
    });

    return {
        loading,
        params,
        // states
        editId,
        user,
        users,
        totalUsers,
        userGroups,
        activePanelUsers,
        // actions
        fetchAllUsers,
        createUser,
        editUser,
        updateUser,
        addUserToDepartments,
        fetchUserPermissionGroups,
        setUserPermissionGroups,
        deleteUser,
        // helpers
        initForm,
        initUsers,

        //getter
        usersList
    };
});
